import {
    BonusSettlementCustomerStatus,
    BonusSettlementSupplierStatus,
    DemandState,
    DocumentStatus,
    ImportSupplierStatus,
    InquiryOfferState,
    SalesOrderStatus,
} from '@synergy/models';

const translationObject: {
    [Key in
        | DocumentStatus
        | DemandState
        | InquiryOfferState
        | SalesOrderStatus
        | BonusSettlementSupplierStatus
        | BonusSettlementCustomerStatus
        | ImportSupplierStatus]: string;
} = {
    [DocumentStatus.Created]: 'app.documentStatus.created',
    [DocumentStatus.Ongoing]: 'app.documentStatus.ongoing',
    [DocumentStatus.Completed]: 'app.documentStatus.completed',
    [DocumentStatus.CompletedWithTransition]: 'app.documentStatus.completedWithTransition',
    [DocumentStatus.CompletedWithoutTransition]: 'app.documentStatus.completedWithoutTransition',
    [DocumentStatus.Canceled]: 'app.documentStatus.canceled',
    [DocumentStatus.Scheduled]: 'app.documentStatus.scheduled',
    [DocumentStatus.Paused]: 'app.documentStatus.paused',
    [DemandState.Open]: 'app.demands.status.open',
    [DemandState.NoDemand]: 'app.demands.status.noDemand',
    [DemandState.DemandReported]: 'app.demands.status.demandReported',
    [DemandState.ClosedNoDemand]: 'app.demands.status.closedNoDemand',
    [DemandState.ClosedWithDemand]: 'app.demands.status.closedWithDemand',
    [InquiryOfferState.Open]: 'app.inquiryOfferState.open',
    [InquiryOfferState.OnGoing]: 'app.inquiryOfferState.onGoing',
    [InquiryOfferState.Declined]: 'app.inquiryOfferState.declined',
    [InquiryOfferState.Closed]: 'app.inquiryOfferState.closed',
    [SalesOrderStatus.CartPending]: 'app.salesOrderStatus.cartPending',
    [SalesOrderStatus.CompletedWithApproval]: 'app.salesOrderStatus.completedWithApproval',
    [SalesOrderStatus.Declined]: 'app.salesOrderStatus.declined',
    [BonusSettlementSupplierStatus.Sent]: 'app.bonusSettlementSupplier.status.sent',
    [BonusSettlementSupplierStatus.Completed]: 'app.bonusSettlementSupplier.status.completed',
    [BonusSettlementSupplierStatus.PartiallySent]: 'app.bonusSettlementSupplier.status.partiallySent',
    [BonusSettlementSupplierStatus.AutomaticallySent]: 'app.bonusSettlementSupplier.status.automaticallySent',
    [BonusSettlementSupplierStatus.Reclaimed]: 'app.bonusSettlementSupplier.status.reclaimed',
    [BonusSettlementCustomerStatus.ReadyToComplete]: 'app.bonusSettlement.Customer.status.readyToComplete',
    [ImportSupplierStatus.OnSchedule]: 'app.importSupplierStatus.onSchedule',
    [ImportSupplierStatus.Overdue]: 'app.importSupplierStatus.overdue',
    [ImportSupplierStatus.NoInterval]: 'app.importSupplierStatus.noInterval',
};

/**
 * The label of a status, or nothing when the value is not one.
 *
 * getDocumentStatusTranslationId answers "new" for anything it does not know,
 * which is right where a document always has a status and wrong where the
 * question is whether a value is one at all — a filter chip would then call
 * every unknown value "Neu".
 */
export const findDocumentStatusTranslationId = (status?: string): string | undefined =>
    status ? (translationObject as Record<string, string>)[status] : undefined;

export const getDocumentStatusTranslationId = (
    status?:
        | DocumentStatus
        | DemandState
        | InquiryOfferState
        | SalesOrderStatus
        | BonusSettlementSupplierStatus
        | BonusSettlementCustomerStatus
        | ImportSupplierStatus
): string => {
    return (status && translationObject[status]) || 'app.documentStatus.new';
};

/**
 * The label of a purchase order's status. An order whose invoice was made from
 * it is "Abgerechnet"; an inquiry with an order keeps "Beendet mit Folgebeleg"
 * (GF 15.10.2026, #2264).
 */
export const getPurchaseOrderStatusTranslationId = (status?: DocumentStatus): string =>
    DocumentStatus.CompletedWithTransition === status
        ? 'app.documentStatus.purchaseOrder.completedWithTransition'
        : getDocumentStatusTranslationId(status);
